import { useTranslation } from 'react-i18next';

import { useApiQuery } from '../../api/hooks';
import type { AppointmentResponse, AppointmentSessionResponse, StatusHistoryResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { shortDate } from '../../i18n/format';
import { useLocalisedName } from '../../i18n/localisedName';
import { clinicDay, clinicTime, longDate } from './clinicTime';

interface AppointmentDetailsProps {
  readonly clinicId: string;
  readonly appointment: AppointmentResponse;
  readonly onClose: () => void;
}

/**
 * One appointment, everything the desk may know about it (CR-28).
 *
 * Until CR-28 an appointment was a row (date, service, doctor, status) with nothing behind it, and
 * its history — kept by the server from the first day — had no screen at all. This shows the
 * booking as made, what actually happened and when (arrived, started, finished, from the status
 * history, each step with who took it), and for a finished session the pulses fired on each area.
 *
 * Nothing clinical (FR-405): the doctor's note, complication flag and settings are not sent to this
 * screen, so it can be shown to reception as it is.
 */
export function AppointmentDetails({ clinicId, appointment, onClose }: AppointmentDetailsProps) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const id = appointment.id ?? '';
  const status = appointment.status ?? 'BOOKED';

  const history = useApiQuery<StatusHistoryResponse[]>(
    ['clinics', clinicId, 'appointments', id, 'history'],
    `/clinics/${clinicId}/appointments/${id}/history`,
  );
  const session = useApiQuery<AppointmentSessionResponse>(
    ['clinics', clinicId, 'appointments', id, 'session'],
    `/clinics/${clinicId}/appointments/${id}/session`,
    { enabled: status === 'COMPLETED' },
  );

  const when = (instant: string | undefined) =>
    instant ? `${shortDate(clinicDay(instant), i18n.language)} ${clinicTime(instant)}` : '—';

  // The session's real start and end, from the history rather than the booking: a session booked for
  // four that started at twenty past ran from twenty past.
  const started = (history.data ?? []).find((step) => step.toStatus === 'IN_PROGRESS')?.changedAt;
  const finished =
    (history.data ?? []).find((step) => step.toStatus === 'COMPLETED')?.changedAt ?? appointment.completedAt;
  const took =
    started && finished ? Math.round((new Date(finished).getTime() - new Date(started).getTime()) / 60_000) : null;

  const service = localised.name({ nameEn: appointment.serviceNameEn, nameAr: appointment.serviceNameAr });
  const area =
    appointment.areaNameEn || appointment.areaNameAr
      ? localised.name({ nameEn: appointment.areaNameEn, nameAr: appointment.areaNameAr })
      : null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('appointment.title')}>
      <div className="modal__panel">
        <header className="modal__header">
          <h2>{t('appointment.title')}</h2>
          <button type="button" className="button button--ghost button--compact" onClick={onClose}>
            {t('common.close')}
          </button>
        </header>

        <div className="modal__body">
          <p>
            <strong>{appointment.patientName}</strong> · {service}
            {area ? ` · ${area}` : ''}{' '}
            <span className="badge badge--muted">{t(`board.status.${status}`)}</span>
          </p>

          <section className="section">
            <dl className="detail-list">
              <div>
                <dt>{t('appointment.date')}</dt>
                <dd>{appointment.startsAt ? longDate(clinicDay(appointment.startsAt), i18n.language) : '—'}</dd>
              </div>
              <div>
                <dt>{t('appointment.bookedFor')}</dt>
                <dd className="ltr">
                  {clinicTime(appointment.startsAt)} – {clinicTime(appointment.endsAt)}
                </dd>
              </div>
              <div>
                <dt>{t('appointment.doctor')}</dt>
                <dd>{appointment.doctorName ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('appointment.room')}</dt>
                <dd>{appointment.roomName ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('appointment.via')}</dt>
                <dd>{t(`appointment.channels.${appointment.bookingChannel ?? 'RECEPTION'}`)}</dd>
              </div>
              {appointment.note ? (
                <div>
                  <dt>{t('appointment.note')}</dt>
                  <dd>{appointment.note}</dd>
                </div>
              ) : null}
              {appointment.policyOverridden ? (
                <div>
                  <dt>{t('appointment.override')}</dt>
                  <dd>{appointment.overrideReason ?? '—'}</dd>
                </div>
              ) : null}
              {status === 'CANCELLED' ? (
                <div>
                  <dt>{t('appointment.cancelled')}</dt>
                  <dd>
                    {t(`appointment.cancellationKinds.${appointment.cancellationKind ?? 'FREE'}`)}
                    {appointment.cancellationReason ? ` · ${appointment.cancellationReason}` : ''}
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>

          {started || status === 'COMPLETED' ? (
            <section className="section">
              <h3 className="section__title">{t('appointment.sessionTitle')}</h3>
              <dl className="detail-list">
                <div>
                  <dt>{t('appointment.started')}</dt>
                  <dd className="ltr">{started ? clinicTime(started) : '—'}</dd>
                </div>
                <div>
                  <dt>{t('appointment.finished')}</dt>
                  <dd className="ltr">{finished ? clinicTime(finished) : '—'}</dd>
                </div>
                {took !== null ? (
                  <div>
                    <dt>{t('appointment.took')}</dt>
                    <dd>{t('appointment.minutes', { n: took })}</dd>
                  </div>
                ) : null}
              </dl>

              {status === 'COMPLETED' ? (
                <AsyncSection query={session} skeletonRows={2}>
                  {(delivered) =>
                    delivered.recorded ? (
                      <dl className="detail-list">
                        {delivered.pulsesDelivered !== undefined && delivered.pulsesDelivered !== null ? (
                          <div>
                            <dt>{t('appointment.pulses')}</dt>
                            <dd>
                              {(delivered.areas ?? []).length > 0 ? (
                                <ul style={{ listStyle: 'none', paddingInlineStart: 0, margin: 0 }}>
                                  {(delivered.areas ?? []).map((line) => (
                                    <li key={line.serviceAreaId}>
                                      {localised.name(line)}: <span className="numeric">{line.pulses}</span>
                                    </li>
                                  ))}
                                  <li>
                                    <strong>{t('appointment.pulsesTotal', { n: delivered.pulsesDelivered })}</strong>
                                  </li>
                                </ul>
                              ) : (
                                <span className="numeric">{delivered.pulsesDelivered}</span>
                              )}
                            </dd>
                          </div>
                        ) : null}
                        {delivered.machineCode || delivered.machineNameEn || delivered.machineNameAr ? (
                          <div>
                            <dt>{t('appointment.machine')}</dt>
                            <dd>
                              {localised.name(
                                { nameEn: delivered.machineNameEn, nameAr: delivered.machineNameAr },
                                delivered.machineCode,
                              )}
                            </dd>
                          </div>
                        ) : null}
                        <div>
                          <dt>{t('appointment.recordedBy')}</dt>
                          <dd>
                            {delivered.recordedByName ?? '—'} · {when(delivered.recordedAt)}
                          </dd>
                        </div>
                      </dl>
                    ) : (
                      <p className="field__hint">{t('appointment.notRecorded')}</p>
                    )
                  }
                </AsyncSection>
              ) : null}
            </section>
          ) : null}

          <section className="section">
            <h3 className="section__title">{t('appointment.historyTitle')}</h3>
            <AsyncSection query={history} skeletonRows={3}>
              {(steps) => (
                <ol style={{ paddingInlineStart: 'var(--space-5)' }}>
                  {steps.map((step, index) => (
                    <li key={`${step.changedAt}-${index}`}>
                      <span className="ltr">{when(step.changedAt)}</span> ·{' '}
                      <strong>{t(`board.status.${step.toStatus ?? 'BOOKED'}`)}</strong>
                      {step.changedByName
                        ? ` · ${t('appointment.by', { name: step.changedByName })}`
                        : ` · ${t(`appointment.channels.${step.channel ?? 'SYSTEM'}`)}`}
                      {step.reason ? ` · ${step.reason}` : ''}
                    </li>
                  ))}
                </ol>
              )}
            </AsyncSection>
          </section>
        </div>
      </div>
    </div>
  );
}
